import { request } from "utils/api";
import { Court } from "./courts";

export type ClubStatus = "private" | "public" | "demo" | "root-owned" | "archived";
export type ClubCapability = "sip" | "public-ground" | "commercial" | "corona-ready" | "company";
// club schema
export interface Club {
    addresseeName: string,
    autoAcceptMembershipRequests: boolean,
    autoInviteMembersToEvents: boolean,
    bookingMinDurationMins: number,
    capabilities: ClubCapability[],
    city: string,
    clubDescription?: string,
    clubEmail?: string,
    clubKey?: string,
    clubName: string,
    clubPhone?: string,
    clubSetupIsCompleted: boolean,
    clubStatus: ClubStatus,
    clubWebsite?: string,
    comingUpBookingMins: number,
    contractsHaveBeenSigned: boolean,
    country: string,
    courts: Court[],
    createdAt: string,
    demoClubCanGoLive?: boolean,
    demoModeEndDate?: string,
    demoModeLoop?: number,
    geofencingRadiusMeters: number,
    id: string,
    internalBookingAtomMins: number,
    latitude: number,
    locationRestrictionType: "none" | "check-in" | "geo-fence",
    longitude: number,
    mainSportForm: string,
    postalCode: string,
    prohibitStandardBookings: boolean,
    restrictAllBookingsToOneHour: boolean,
    shortName: string,
    stateOrProvince: string,
    street: string,
    timeZone: string,
    verificationFailureReason?: string,
    verificationStatus: "none" | "processing" | "failed" | "verified",
    clubLogoUrl?: string,
    increasedDataProtections?: IncreasedDataProtections[],
}

export interface MinimalClubInfo {
    id: string;
    createdAt: Date;

    clubName: string;
    shortName: string;
    mainSportForm: string;

    street: string;
    postalCode: string;
    city: string;
    country: string;

    clubStatus: ClubStatus;
    capabilities: ClubCapability[];

    clubPictureUrl: string;
    galleryMediaUrls: string[];
    shouldUseClubLogoAsIcon?: boolean;
    clubLogoUrl?: string;
}

export enum IncreasedDataProtections {
    ACTIVITIES = "activities",
    BOOKINGS = "bookings",
    DOCUMENTS = "documents",
    MEMEBERS = "members",
    COURTS = "courts",
    CLUB = "club",
    B2B_FEATURES = "b2b-features",
    SUBSCRIPTION = "subscription",
    INVOICE_METHOD = "showInvoiceMethod",
}

export interface CompanyBudgetDefault {
    monthlyCompanyTotalBudgetCents: number,
    monthlyCompanyMemberDefaultBudgetCents: number,
}

export interface CompanyBudget {
    monthDate: Date,
    totalBudgetCents: number,
    usedBudgetCents: number,
}

export interface UserInfo {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    profilePictureUrl: string;
    isAlias: boolean;
    mainAccount?: {
        id: string,
        firstName: string,
        lastName: string,
        profilePictureUrl: string,
        email: string,
    },
}

export interface MemberBudget {
    id: string;
    monthDate: Date | string,
    totalMemberBudgetCents: number;
    usedMemberBudgetCents: number;
    clubName: string;
    user: UserInfo;
    monthlyCompanyMemberBudgetCents?: number;
    site?: string;
}

// Fetch default budget GET /company/budgets/default
export const getCompanyBudgetDefault = (data: {
    clubId: string,
}) => request<CompanyBudgetDefault>({
    data,
    endpoint: "/company/budgets/default",
    method: "get",
});

// Fetch default budget GET /company/budgets/default
export const updateCompanyBudgetDefault = (data: {
    clubId: string,
    monthlyCompanyTotalBudgetCents: number,
    monthlyCompanyMemberDefaultBudgetCents: number,
    applyToCurrentMonth: boolean,
}) => request<CompanyBudgetDefault>({
    data,
    endpoint: "/company/budgets/default",
    method: "patch",
});

// GET /company/budgets
export const getCompanyBudgets = (data: {
    clubId: string,
    toDate?: string,
    limit?: number,
    offset?: number,
}) => request<{ data: CompanyBudget[], totalCount: number }>({
    data,
    endpoint: "/company/budgets",
    method: "get",
});

// GET /company/budgets/members
export const getCompanyMemberBudget = (data: {
    clubId: string,
    limit?: number,
    offset?: number,
    query?: string,
    sortKey?: "firstName" | "lastName",
    sortOrder?: "ASC" | "DESC",
}) => request<{ data: MemberBudget[], totalCount: number }>({
    data,
    endpoint: "/company/budgets/members",
    method: "get",
});

// patch /company/budgets/members
export const updateCompanyMemberBudget = (data: {
    clubId: string,
    monthlyCompanyMemberBudgetCents: number,
    applyToCurrentMonth?: boolean,
    userIds?: string[],
    query?: string,
}) => request<any>({
    data,
    endpoint: "/company/budgets/members",
    method: "patch",
});

// fetch dashboard club (GET /auth/dashboard/me)
export const getDashboardClub = () => request<Club>({
    endpoint: "/auth/dashboard/me",
    method: "get",
});

// create a pending demo, previously club proposal (POST /clubs/pending-demos)
export const postPendingDemo = (data: {
    city: string,
    clubName: string,
    country: string,
    mainSportForm: string,
    postalCode: string,
    stateOrProvince: string,
    street: string,
    clubType?: string;
}) => request<Club>({
    data,
    endpoint: "/clubs/pending-demos",
    method: "post",
});
